import type { AuthResponse, FeatureFlag, Permission } from "@klyra/shared";
/**
 * Auth state — persisted to localStorage so reloads keep the session.
 *
 * Note: storing the access token in localStorage is acceptable for an
 * internal/staff PWA where XSS is mitigated by tight CSP + Tailwind/React's
 * default escaping. For the future patient-facing flow we'll move to
 * httpOnly cookies.
 */
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { clearAllDrafts, clearOfflineCache } from "../lib/offline-cache.ts";
import { clearQueryCache } from "../lib/query-client.ts";

interface AuthState {
	user: AuthResponse["user"] | null;
	clinic: AuthResponse["clinic"] | null;
	accessToken: string | null;
	refreshToken: string | null;
	/** Set when a super-admin is impersonating this facility (shows a banner). */
	impersonation: { facility_name: string } | null;

	setSession: (data: AuthResponse) => void;
	startImpersonation: (data: AuthResponse, facilityName: string) => void;
	setPermissions: (permissions: string[]) => void;
	clearSession: () => void;
	isAuthenticated: () => boolean;
	hasPermission: (p: Permission) => boolean;
	hasFeature: (f: FeatureFlag) => boolean;
	/** True if the clinic's specialty matches any of the given business types. */
	isBusinessType: (...types: string[]) => boolean;
}

export const useAuthStore = create<AuthState>()(
	persist(
		(set, get) => ({
			user: null,
			clinic: null,
			accessToken: null,
			refreshToken: null,
			impersonation: null,

			setSession: (data) => {
				// Tenant/branch switch in the SAME tab: if the clinic actually changed,
				// drop the in-memory query cache so the previous branch's data can't
				// flash inside staleTime (F2.3). A token refresh keeps the same
				// clinic.id, so the cache is preserved (no needless refetch storm).
				// Defence-in-depth: the BranchSwitcher also hard-reloads today, but this
				// guard protects any future caller that switches tenant without a reload.
				const prevClinicId = get().clinic?.id;
				const nextClinicId = data.clinic?.id;
				if (prevClinicId && nextClinicId && prevClinicId !== nextClinicId) {
					clearQueryCache();
				}
				set({
					user: data.user,
					clinic: data.clinic,
					accessToken: data.tokens.access_token || get().accessToken,
					refreshToken: data.tokens.refresh_token || get().refreshToken,
				});
			},

			// Start a fresh clinic session AS the facility owner (super-admin support).
			// Replaces any current clinic session; flagged so the app shows a banner.
			startImpersonation: (data, facilityName) => {
				// Drop the in-memory query cache — we're switching tenants in the
				// same tab, and queries inside staleTime would otherwise show the
				// previous facility's data without refetching.
				clearQueryCache();
				set({
					user: data.user,
					clinic: data.clinic,
					accessToken: data.tokens.access_token,
					refreshToken: data.tokens.refresh_token || null,
					impersonation: { facility_name: facilityName },
				});
			},

			setPermissions: (permissions) => {
				const c = get().clinic;
				if (!c) return;
				set({ clinic: { ...c, permissions } });
			},

			clearSession: () => {
				// Wipe the offline read cache + any form drafts so a logged-out device
				// keeps no PHI — and the in-memory query cache, so logging into a
				// different account in the same tab can't flash the old tenant's data.
				clearQueryCache();
				void clearOfflineCache();
				void clearAllDrafts();
				set({
					user: null,
					clinic: null,
					accessToken: null,
					refreshToken: null,
					impersonation: null,
				});
			},

			isAuthenticated: () => Boolean(get().accessToken && get().user),

			hasPermission: (p) => {
				const c = get().clinic;
				if (!c) return false;
				// Defensive: pre-permissions sessions persisted with no array
				if (!Array.isArray(c.permissions)) return c.role === "owner";
				return c.permissions.includes(p);
			},

			// Default-enabled: a feature is on unless the admin set it to false.
			hasFeature: (f) => {
				const c = get().clinic;
				return c?.feature_flags?.[f] !== false;
			},

			isBusinessType: (...types) => {
				const bt = get().clinic?.business_type;
				return bt ? types.includes(bt) : false;
			},
		}),
		{
			name: "klyra.auth",
			storage: createJSONStorage(() => localStorage),
			partialize: (state) => ({
				user: state.user,
				clinic: state.clinic,
				accessToken: state.accessToken,
				refreshToken: state.refreshToken,
				impersonation: state.impersonation,
			}),
		},
	),
);
